import { createElement, useCallback, useMemo } from 'react'
import { ChevronRight } from 'lucide-react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import TaskSidebar from '@/components/app/sidebars/task'
import { usePages, useRootProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { ProjectIcon } from '@/lib/utils/projectIcon'
import { StarToggle } from '@/components/app/StarToggle'
import { BreadcrumbSubPagesDropdown } from '@/routes/features/pages/components/BreadcrumbSubPagesDropdown'
import { CollapsibleAncestorBreadcrumbs } from '@/routes/features/pages/components/CollapsibleAncestorBreadcrumbs'
import { PageDetailsView } from '@/routes/features/pages/components/PageDetailsView'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
)({
  component: SpacePageDetailsPage,
})

export default function SpacePageDetailsPage() {
  const { workspaceSlug, spaceKey, pageSlug } = Route.useParams()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((state) => state.workspaceId)
  const userId = useLoginStore((state) => state.userId)

  const { projects: rootProjects } = useRootProjects()
  const { pages, isLoading: pagesLoading } = usePages()

  const space = useMemo(() => {
    return rootProjects.find((p) => {
      if (p.isDeleted) return false
      if (p.projectKey.toLowerCase() !== spaceKey.toLowerCase()) return false
      if (workspaceId && p.envId !== workspaceId) return false
      return true
    }) ?? null
  }, [rootProjects, spaceKey, workspaceId])

  const page = useMemo<PageDocument | null>(() => {
    if (!space) return null
    return pages.find((p) => {
      if (p.isDeleted) return false
      if (p.slug !== pageSlug) return false
      if (p.projectId !== space.id) return false
      return true
    }) ?? null
  }, [pages, pageSlug, space])

  const ancestors = useMemo(() => {
    if (!page || !space) return []
    const chain: Array<PageDocument> = []
    let current = page
    while (current.parentPageId) {
      const parent = pages.find((p) => p.id === current.parentPageId && !p.isDeleted)
      if (!parent) break
      chain.unshift(parent)
      current = parent
    }
    return chain
  }, [page, pages, space])

  const breadcrumb = useMemo(() => {
    if (!space || !page) return null
    return (
      <>
        <Link
          to="/$workspaceSlug/spaces/$spaceKey"
          params={{ workspaceSlug, spaceKey }}
          className="flex items-center gap-1 hover:text-foreground transition-colors truncate"
        >
          <ProjectIcon iconType={space.iconType} icon={space.icon} iconColor={space.iconColor} size={14} />
          <span className="truncate">{space.name}</span>
        </Link>
        <CollapsibleAncestorBreadcrumbs
          ancestors={ancestors}
          projectId={space.id}
          pages={pages}
          renderLink={(ancestor) => (
            <Link
              to="/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug"
              params={{ workspaceSlug, spaceKey, pageSlug: ancestor.slug }}
              className="hover:text-foreground transition-colors truncate"
            >
              {ancestor.title || 'Untitled'}
            </Link>
          )}
          onNavigate={(slug) => navigate({
            to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
            params: { workspaceSlug, spaceKey, pageSlug: slug },
          })}
        />
        <ChevronRight className="size-2.5 flex-shrink-0" />
        <span className="flex items-center gap-1.5 truncate">
          <span className="truncate">{page.title || 'Untitled'}</span>
          <StarToggle entityType="page" entityId={page.id} size={14} />
          <BreadcrumbSubPagesDropdown
            currentPageId={page.id}
            projectId={space.id}
            pages={pages}
            onNavigate={(slug) => navigate({
              to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
              params: { workspaceSlug, spaceKey, pageSlug: slug },
            })}
          />
        </span>
      </>
    )
  }, [space, page, ancestors, workspaceSlug, spaceKey, pages, navigate])

  const sidebar = useMemo(() => createElement(TaskSidebar), [])

  const handleNavigateToSubPage = useCallback((slug: string) => {
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
      params: { workspaceSlug, spaceKey, pageSlug: slug },
    })
  }, [navigate, workspaceSlug, spaceKey])

  const handleNavigateAfterDelete = useCallback((parentPage: PageDocument | null) => {
    if (parentPage) {
      navigate({
        to: '/$workspaceSlug/spaces/$spaceKey/pages/$pageSlug',
        params: { workspaceSlug, spaceKey, pageSlug: parentPage.slug },
      })
      return
    }
    navigate({
      to: '/$workspaceSlug/spaces/$spaceKey',
      params: { workspaceSlug, spaceKey },
    })
  }, [navigate, workspaceSlug, spaceKey])

  if (pagesLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!space || !page || !workspaceId) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center px-4">
        <div className="text-center text-muted-foreground">
          Page not found or may have been removed.
        </div>
      </div>
    )
  }

  return (
    <PageDetailsView
      page={page}
      project={space}
      pages={pages}
      workspaceId={workspaceId}
      userId={userId}
      pageSlug={pageSlug}
      sidebar={sidebar}
      breadcrumb={breadcrumb}
      layoutDeps={[space.id, space.name, space.icon, space.iconType, space.iconColor, workspaceSlug, spaceKey, pageSlug, page.id, page.title, page.parentPageId, ancestors.length]}
      onNavigateToSubPage={handleNavigateToSubPage}
      onNavigateAfterDelete={handleNavigateAfterDelete}
    />
  )
}
